import React, { useCallback } from 'react';
import { Box, Flex, TextV2, Radio, DrawerV2, PopoverV2, useDisclosure, Divider, ButtonV2 } from '@withjoy/joykit';
import { ChevronDown } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { SortOption } from '@apps/accommodations/AccommodationsConsole.types';
import { DropdownButton } from './DropdownButton';
import { useTranslation } from '@shared/core';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

interface SortComponentProps {
  selectedValue: SortOption;
  onChange: (value: SortOption) => void;
}

export const SortComponent: React.FC<SortComponentProps> = ({ selectedValue, onChange }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation('accommodationsConsole');
  const sortTrans = t('sort');
  const isMobileOrTablet = useIsMobileOrTablet();

  const handleSortChange = useCallback(
    (value: SortOption) => {
      onChange(value);
      if (!isMobileOrTablet) {
        onClose();
      }
    },
    [onChange, isMobileOrTablet, onClose]
  );

  const sortOptions = [
    { label: sortTrans.couplesPicks(), value: SortOption.COUPLES_PICKS },
    { label: sortTrans.priceLowToHigh(), value: SortOption.PRICE_LOW_TO_HIGH },
    { label: sortTrans.priceHighToLow(), value: SortOption.PRICE_HIGH_TO_LOW },
    { label: sortTrans.mostSavings(), value: SortOption.MOST_SAVINGS },
    { label: sortTrans.distance(), value: SortOption.DISTANCE }
  ];

  const SortContent = (
    <Box padding={isMobileOrTablet ? pxToRem(24) : 7} minWidth={isMobileOrTablet ? '100%' : pxToRem(380)}>
      <TextV2 typographyVariant="hed2" marginBottom={7}>
        {sortTrans.title()}
      </TextV2>
      <Box>
        <Flex flexDirection="column" gap={5}>
          {sortOptions.map(option => (
            <Flex key={option.value} alignItems="center">
              <Radio
                onChange={() => handleSortChange(option.value)}
                id={`sort-${option.value.toLowerCase()}`}
                checked={selectedValue === option.value}
                value={option.value}
                activeColor="#4951EF"
                name="sortBy"
                label={option.label}
                style={{ fontSize: pxToRem(16) }}
              />
            </Flex>
          ))}
        </Flex>
      </Box>
    </Box>
  );

  const filterButtonIconSize = 16;
  const selectedOption = sortOptions.find(option => option.value === selectedValue);
  const trigger = <DropdownButton text={`${sortTrans.sortPrefix()} ${selectedOption?.label}`} endIcon={<ChevronDown size={filterButtonIconSize} />} />;

  if (isMobileOrTablet) {
    return (
      <Flex position="relative">
        <Box onClick={onOpen}>{trigger}</Box>
        <DrawerV2
          isOpen={isOpen}
          onClose={onClose}
          anchor="bottom"
          size="auto"
          overrides={{ Content: { props: { borderTopLeftRadius: 4, borderTopRightRadius: 4, height: 'auto' } } }}
        >
          <DrawerV2.Body padding={0}>{SortContent}</DrawerV2.Body>
          <Divider />
          <DrawerV2.Footer>
            <ButtonV2 onClick={onClose} width="100%" variant="solid" shape="rounded" backgroundColor="newUITest1">
              {sortTrans.done()}
            </ButtonV2>
          </DrawerV2.Footer>
        </DrawerV2>
      </Flex>
    );
  }

  return (
    <Flex position={isMobileOrTablet ? 'unset' : 'relative'}>
      <Box onClick={onOpen}>{trigger}</Box>
      <PopoverV2
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom"
        overrides={{
          Root: { props: { minWidth: pxToRem(280), top: isMobileOrTablet ? 80 : 8, left: isMobileOrTablet ? -28 : 'none', right: isMobileOrTablet ? 'none' : 0 } },
          Content: {
            props: {
              borderRadius: pxToRem(24),
              style: { userSelect: 'none' }
            }
          }
        }}
      >
        <PopoverV2.Content>{SortContent}</PopoverV2.Content>
      </PopoverV2>
    </Flex>
  );
};
